





import React, { useState, useEffect, useCallback } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  Eye, EyeOff, Pencil, Trash2, X, Plus, Phone, Mail, Search, RotateCcw,
  ChevronLeft, ChevronRight, User, Briefcase, Heart, 
  CheckCircle, Loader2, ArrowLeft, ShieldCheck, Lock
} from "lucide-react";

const Staff = () => {
  // --- VIEW STATE ---
  const [view, setView] = useState("list"); 
  const [showPassword, setShowPassword] = useState(false);
  
  // --- DATA STATES ---
  const [staffList, setStaffList] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState(""); 
  const [searchTerm, setSearchTerm] = useState(""); 
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewStaff, setViewStaff] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const initialFormState = {
    staffId: null,
    staffCode: "",
    fullName: "",
    email: "",
    phoneNumber: "",
    designationId: "", 
    branchId: "",
    referenceId: "",
    fatherName: "",
    gender: "Male",
    dateofBirth: "",
    martialStatus: "Single",
    address: "",
    academic: "",
    technical: "",
    experience: "",
    panCard: "",
    aadharNo: "",
    hobbies: "",
    languagesKnown: "",
    nomineeName: "",
    nomineePhone: "",
    nomineeDateOfBirth: "",
    nomineeAddress: "",
    relationship: "",
    status: "ACTIVE",
    profileImageName: "",
    joiningDate: "",
    exitDate: "",
    notes: "",
    payMode: "Cash",
    isLogin: false,
    password: ""
  };
  
  const [formData, setFormData] = useState(initialFormState);

  // --- FETCHING ---
  const fetchDesignations = async () => {
    try {
        const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.DESIGNATION_GET_ALL}?pageSize=100`);
        const data = await res.json();
        setDesignations(data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchBranches = async () => {
    try {
        const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.BRANCH_GET_ALL}?pageSize=100`);
        const data = await res.json();
        setBranches(data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.STAFF_GET_ALL}?pageNumber=${currentPage}&pageSize=${pageSize}&searchTerm=${searchTerm}`;
      const response = await fetch(url);
      const result = await response.json();
      setStaffList(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchStaff();
    fetchDesignations();
    fetchBranches();
  }, [fetchStaff]);

  // --- HANDLERS ---
  const handleSearchClick = () => { setSearchTerm(inputValue); setCurrentPage(1); };
  const handleClearSearch = () => { setInputValue(""); setSearchTerm(""); setCurrentPage(1); };

  const openForm = (staff = null) => {
    setErrors({});
    setShowPassword(false);
    if (staff) {
      const formatDate = (dateStr) => dateStr ? dateStr.split('T')[0] : "";
      setFormData({
        ...staff,
        designationId: staff.designationId || "",
        branchId: staff.branchId || "",
        dateofBirth: formatDate(staff.dateofBirth),
        joiningDate: formatDate(staff.joiningDate),
        exitDate: formatDate(staff.exitDate),
        nomineeDateOfBirth: staff.nomineeDateOfBirth || "",
        password: ""
      });
    } else {
      setFormData(initialFormState);
    }
    setView("form");
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    if (errors[name]) setErrors({ ...errors, [name]: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // VALIDATION LOGIC
    let tempErrors = {};
    if (!formData.fullName) tempErrors.fullName = true;
    if (!formData.phoneNumber) tempErrors.phoneNumber = true;
    if (!formData.staffCode) tempErrors.staffCode = true;
    
    if (Object.keys(tempErrors).length > 0) {
        setErrors(tempErrors);
        return Swal.fire("Required", "Please fill all mandatory fields marked in red.", "warning");
    }
    
    setSaving(true);
    
    // Cleanup: Backend Guid? and DateTime? don't like empty strings ""
    const payload = { ...formData };
    if (!payload.designationId || payload.designationId === "") payload.designationId = null;
    if (!payload.branchId || payload.branchId === "") payload.branchId = null;
    if (!payload.referenceId || payload.referenceId === "") payload.referenceId = null;
    if (!payload.dateofBirth || payload.dateofBirth === "") payload.dateofBirth = null;
    if (!payload.joiningDate || payload.joiningDate === "") payload.joiningDate = null;
    if (!payload.exitDate || payload.exitDate === "") payload.exitDate = null;
    if (!payload.nomineeDateOfBirth || payload.nomineeDateOfBirth === "") payload.nomineeDateOfBirth = null;

    try {
      const res = await fetch(APIConfig.APIACTIVEURL + APIConfig.STAFF_BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.success) {
        setView("list");
        fetchStaff();
        Swal.fire({ icon: 'success', title: 'Success', text: result.message, timer: 1500, showConfirmButton: false });
      } else {
        Swal.fire('Error', result.message, 'error');
      }
    } catch (err) { 
        console.error(err);
        Swal.fire('Error', 'Connection to server failed', 'error');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title: 'Remove Staff?', text: 'This will also delete their login account.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444' });
    if (result.isConfirmed) {
      await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.STAFF_BASE}/${id}`, { method: "DELETE" });
      fetchStaff();
    }
  };

  // --- STYLES ---
  const styles = {
    container: { padding: "40px 60px", backgroundColor: "#f4f7fe", minHeight: "calc(100vh - 70px)", fontFamily: "'Inter', sans-serif" },
    card: { backgroundColor: "#fff", borderRadius: "24px", boxShadow: "0 10px 30px rgba(0,0,0,0.02)", overflow: "hidden", border: '1px solid #f1f5f9' },
    formSection: { background: '#fff', border: '1px solid #f1f5f9', borderRadius: '24px', padding: '30px', marginBottom: '30px' },
    label: { display: "block", fontSize: "11px", fontWeight: "800", color: "#a3aed0", marginBottom: "8px", textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { padding: "14px", borderRadius: "12px", border: "1.5px solid #e2e8f0", outline: "none", fontSize: "15px", width: '100%', boxSizing: 'border-box', backgroundColor: '#fff' },
    btnPrimary: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "14px 28px", borderRadius: "14px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "10px" },
    btnReset: { backgroundColor: "#fff", color: "#64748b", border: "1.5px solid #e2e8f0", padding: "0 24px", borderRadius: "14px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px" },
    sectionHeader: { display:'flex', alignItems:'center', gap:'10px', fontSize:'17px', fontWeight:'900', color:'#1b2559', marginBottom:'25px' },
    // Toggle Switch Styles
    switch: { position: 'relative', display: 'inline-block', width: '50px', height: '26px' },
    switchInput: { opacity: 0, width: 0, height: 0 },
    slider: (checked) => ({ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: checked ? '#4f46e5' : '#cbd5e1', transition: '.4s', borderRadius: '34px' }),
    sliderCircle: (checked) => ({ position: 'absolute', height: '18px', width: '18px', left: checked ? '28px' : '4px', bottom: '4px', backgroundColor: 'white', transition: '.4s', borderRadius: '50%' })
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        
        {/* VIEW: STAFF LIST */}
        {view === "list" && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '35px' }}>
              <div>
                <h1 style={{ fontSize: '30px', fontWeight: '900', color: '#1b2559', margin: 0 }}>Staff Directory</h1>
                <p style={{ color: '#a3aed0', marginTop: '5px' }}>Manage employees and portal permissions.</p>
              </div>
              <button style={styles.btnPrimary} onClick={() => openForm()}>
                <Plus size={20} /> New Staff
              </button>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '30px' }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '15px', top: '15px', color: '#cbd5e1' }} />
                    <input style={{ ...styles.input, paddingLeft: '45px' }} placeholder="Search name or code..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()} />
                </div>
                <button style={styles.btnPrimary} onClick={handleSearchClick}>Search</button>
                {searchTerm && (
                  <button onClick={handleClearSearch} style={styles.btnReset}>
                    <RotateCcw size={18}/> Reset
                  </button>
                )}
            </div>

            <div style={styles.card}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #f8fafc', background: '#fcfcfd' }}>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>STAFF DETAILS</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>DESIGNATION</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>BRANCH</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>WEB LOGIN</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0', textAlign:'center' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" style={{ textAlign:'center', padding:'80px' }}><Loader2 className="animate-spin" color="#4f46e5" /></td></tr>
                  ) : staffList.map(s => (
                    <tr key={s.staffId} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '20px 24px' }}>
                        <div style={{ fontWeight: '800', color: '#1b2559' }}>{s.fullName}</div>
                        <div style={{ fontSize: '12px', color: '#a3aed0' }}>{s.staffCode} | {s.phoneNumber}</div>
                      </td>
                      <td style={{ padding: '20px 24px', color:'#4f46e5', fontWeight:'700' }}>{s.designation?.designationName || '---'}</td>
                      <td style={{ padding: '20px 24px', color:'#1b2559', fontWeight:'700' }}>{s.branch?.branchName || '---'}</td>
                      <td style={{ padding: '20px 24px' }}>
                        {s.isLogin ? <span style={{fontSize:'10px', fontWeight:'900', color:'#059669', background:'#dcfce7', padding:'4px 10px', borderRadius:'15px'}}>ENABLED</span> : <span style={{fontSize:'10px', fontWeight:'900', color:'#a3aed0', background:'#f4f7fe', padding:'4px 12px', borderRadius:'15px'}}>DISABLED</span>}
                      </td>
                      <td style={{ padding: '20px 24px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button onClick={() => {setViewStaff(s); setShowViewModal(true)}} style={{ border: 'none', background: '#f4f7fe', color: '#1b2559', padding: '10px', borderRadius: '10px', cursor: 'pointer' }}><Eye size={16}/></button>
                            <button onClick={() => openForm(s)} style={{ border: 'none', background: '#eef2ff', color: '#4f46e5', padding: '10px', borderRadius: '10px', cursor: 'pointer' }}><Pencil size={16}/></button>
                            <button onClick={() => handleDelete(s.staffId)} style={{ border: 'none', background: '#fff1f2', color: '#ef4444', padding: '10px', borderRadius: '10px', cursor: 'pointer' }}><Trash2 size={16}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems:'center', background: '#fcfcfd' }}>
                <span style={{color:'#a3aed0', fontSize:'14px', fontWeight: '700'}}>Page {currentPage} of {totalPages}</span>
                <div style={{display:'flex', gap:'8px'}}>
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c - 1)} style={{padding:'10px 20px', borderRadius:'12px', border:'1px solid #e2e8f0', background:'#fff', cursor:'pointer', fontWeight:'700'}}>Previous</button>
                    <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(c => c + 1)} style={{padding:'10px 20px', borderRadius:'12px', border:'1px solid #e2e8f0', background:'#fff', cursor:'pointer', fontWeight:'700'}}>Next</button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* VIEW: FULL PAGE FORM */}
        {view === "form" && (
            <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                <button onClick={() => setView("list")} style={{ border:'none', background:'none', color:'#a3aed0', fontWeight:'700', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px', marginBottom:'25px' }}>
                    <ArrowLeft size={20} /> Back to Directory
                </button>

                <div style={{ marginBottom: '35px' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: '900', color: '#1b2559', margin: 0 }}>{formData.staffId ? "Modify Staff Record" : "New Staff Enrollment"}</h2>
                    <p style={{ color: '#a3aed0' }}>Fill all fields to ensure complete system records.</p>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* SECTION 1: PERSONAL */}
                    <div style={styles.formSection}>
                        <div style={styles.sectionHeader}><User color="#4f46e5" /> Personal Information</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                            <div><label style={styles.label}>Staff Code *</label><input name="staffCode" style={{...styles.input, borderColor: errors.staffCode ? '#ef4444' : '#e2e8f0'}} value={formData.staffCode} onChange={handleInputChange} placeholder="Ex: ST001" /></div>
                            <div><label style={styles.label}>Full  Name *</label><input name="fullName" style={{...styles.input, borderColor: errors.fullName ? '#ef4444' : '#e2e8f0'}} value={formData.fullName} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Phone No(Username) *</label><input name="phoneNumber" style={{...styles.input, borderColor: errors.phoneNumber ? '#ef4444' : '#e2e8f0'}} value={formData.phoneNumber} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Email ID</label><input name="email" style={styles.input} value={formData.email} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Father / Guardian Name</label><input name="fatherName" style={styles.input} value={formData.fatherName} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Gender</label>
                                <select name="gender" style={styles.input} value={formData.gender} onChange={handleInputChange}>
                                    <option value="Male">Male</option><option value="Female">Female</option>
                                </select>
                            </div>
                            <div><label style={styles.label}>Date of Birth</label><input name="dateofBirth" type="date" style={styles.input} value={formData.dateofBirth} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Marital Status</label>
                                <select name="martialStatus" style={styles.input} value={formData.martialStatus} onChange={handleInputChange}>
                                    <option value="Single">Single</option><option value="Married">Married</option>
                                </select>
                            </div>
                            <div style={{gridColumn:'span 3'}}><label style={styles.label}>Permanent Address</label><textarea name="address" style={{...styles.input, height:'80px', resize: 'none'}} value={formData.address} onChange={handleInputChange} /></div>
                        </div>
                    </div>

                    {/* SECTION 2: PROFESSIONAL */}
                    <div style={styles.formSection}>
                        <div style={styles.sectionHeader}><Briefcase color="#4f46e5" /> Professional Details</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                            <div><label style={styles.label}>Designation</label>
                                <select name="designationId" style={styles.input} value={formData.designationId} onChange={handleInputChange}>
                                    <option value="">Select Role...</option>
                                    {designations.map(d => <option key={d.designationId} value={d.designationId}>{d.designationName}</option>)}
                                </select>
                            </div>
                            <div><label style={styles.label}>Select Branch</label>
                                <select name="branchId" style={styles.input} value={formData.branchId} onChange={handleInputChange}>
                                    <option value="">Select Branch...</option>
                                    {branches.map(b => <option key={b.branchId} value={b.branchId}>{b.branchName}</option>)}
                                </select>
                            </div>
                            <div><label style={styles.label}>Date of Joining</label><input name="joiningDate" type="date" style={styles.input} value={formData.joiningDate} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Academic Background</label><input name="academic" style={styles.input} value={formData.academic} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Technical Skills</label><input name="technical" style={styles.input} value={formData.technical} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Work Experience</label><input name="experience" style={styles.input} value={formData.experience} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Aadhar Number</label><input name="aadharNo" style={styles.input} value={formData.aadharNo} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>PAN Card Number</label><input name="panCard" style={styles.input} value={formData.panCard} onChange={handleInputChange} /></div>
                            
                            <div><label style={styles.label}>Personal Hobbies</label><input name="hobbies" style={styles.input} value={formData.hobbies} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Languages Known</label><input name="languagesKnown" style={styles.input} value={formData.languagesKnown} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Payment Mode</label><input name="payMode" style={styles.input} value={formData.payMode} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Status</label>
                                <select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}>
                                    <option value="ACTIVE">ACTIVE</option><option value="INACTIVE">INACTIVE</option>
                                </select>
                            </div>
                            <div style={{gridColumn:'span 3'}}><label style={styles.label}>Notes</label><textarea name="notes" style={{...styles.input, height:'60px', resize: 'none'}} value={formData.notes} onChange={handleInputChange} /></div>
                            
                        </div>
                    </div>

                    {/* SECTION 3: NOMINEE */}
                    <div style={styles.formSection}>
                        <div style={styles.sectionHeader}> Nominee Details</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                            <div><label style={styles.label}>Nominee Full Name</label><input name="nomineeName" style={styles.input} value={formData.nomineeName} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Relationship to Staff</label><input name="relationship" style={styles.input} value={formData.relationship} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Nominee Contact Phone</label><input name="nomineePhone" style={styles.input} value={formData.nomineePhone} onChange={handleInputChange} /></div>
                            <div><label style={styles.label}>Nominee Date of Birth</label><input name="nomineeDateOfBirth" type="date" style={styles.input} value={formData.nomineeDateOfBirth} onChange={handleInputChange} /></div>
                            <div style={{gridColumn:'span 2'}}><label style={styles.label}>Nominee Permanent Address</label><input name="nomineeAddress" style={styles.input} value={formData.nomineeAddress} onChange={handleInputChange} /></div>
                        </div>
                    </div>

                    {/* SECTION 4: LOGIN ACCESS (Moved to Last) */}
                    <div style={{ ...styles.formSection, backgroundColor: '#f8fafc', border: '1.5px dashed #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', fontSize:'17px', fontWeight:'900', color:'#1b2559' }}><ShieldCheck color="#4f46e5" />Login Access</div>
                            <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                                <span style={{ fontWeight: '800', color: formData.isLogin ? '#4f46e5' : '#a3aed0', fontSize: '13px' }}>{formData.isLogin ? "ON" : "OFF"}</span>
                                <label style={styles.switch}>
                                    <input type="checkbox" name="isLogin" checked={formData.isLogin} onChange={handleInputChange} style={styles.switchInput} />
                                    <span style={styles.slider(formData.isLogin)}>
                                        <span style={styles.sliderCircle(formData.isLogin)}></span>
                                    </span>
                                </label>
                            </div>
                        </div>
                        {formData.isLogin && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '30px' }}>
                                <div>
                                    <label style={styles.label}>Username (PHONE No)</label>
                                    <input disabled style={{...styles.input, backgroundColor:'#f1f5f9', color: '#64748b', fontWeight: '600'}} value={formData.phoneNumber || "Provide phone number above"} />
                                </div>
                                <div>
                                    <label style={styles.label}>Set Password</label>
                                    <div style={{ position: 'relative' }}>
                                        <input type={showPassword ? "text" : "password"} name="password" style={styles.input} value={formData.password} onChange={handleInputChange} placeholder="Create password" />
                                        <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '15px', top: '15px', background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1' }}>
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', paddingBottom: '80px' }}>
                        <button type="button" onClick={() => setView("list")} style={{ padding: '14px 40px', borderRadius: '14px', border: '1.5px solid #e2e8f0', background: '#fff', fontWeight: '700', cursor: 'pointer', color: '#64748b' }}>Cancel</button>
                        <button type="submit" disabled={saving} style={styles.btnPrimary}>
                            {saving ? <Loader2 className="animate-spin" /> : <CheckCircle />} {saving ? "Saving Record..." : "Confirm & Save Profile"}
                        </button>
                    </div>
                </form>
            </div>
        )}

        {/* VIEW PROFILE MODAL */}
        {showViewModal && viewStaff && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5000 }}>
                <div style={{ backgroundColor: '#fff', padding: '40px', borderRadius: '32px', width: '650px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                        <h2 style={{ margin: 0, fontWeight: '900', color: '#1b2559' }}>Employee Profile</h2>
                        <X size={24} style={{ cursor: 'pointer', color: '#cbd5e1' }} onClick={() => setShowViewModal(false)} />
                    </div>
                    <div style={{ display: 'grid', gap: '25px' }}>
                        <div style={{ padding: '30px', background: 'linear-gradient(135deg, #f4f7fe 0%, #fff 100%)', borderRadius: '24px', textAlign: 'center', border: '1px solid #eef2ff' }}>
                            <div style={{ fontSize: '26px', fontWeight: '900', color: '#1b2559', marginBottom: '5px' }}>{viewStaff.fullName}</div>
                            <div style={{ color: '#4f46e5', fontWeight: '800', textTransform: 'uppercase', fontSize: '13px', letterSpacing: '1px' }}>{viewStaff.designation?.designationName || 'No Role Assigned'}</div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div><label style={styles.label}>Employee Code</label><div style={{fontWeight:'700', color: '#1b2559'}}>{viewStaff.staffCode}</div></div>
                            <div><label style={styles.label}>Mobile Phone</label><div style={{fontWeight:'700', color: '#1b2559'}}>{viewStaff.phoneNumber}</div></div>
                            <div><label style={styles.label}>Assigned Branch</label><div style={{fontWeight:'700', color: '#1b2559'}}>{viewStaff.branch?.branchName || 'N/A'}</div></div>
                            <div><label style={styles.label}>Current Status</label><div style={{fontWeight:'700', color: '#1b2559'}}>{viewStaff.status}</div></div>
                        </div>
                        <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '20px' }}>
                            <label style={styles.label}>Emergency Nominee</label>
                            <div style={{ fontWeight: '800', color: '#1b2559', fontSize: '15px' }}>{viewStaff.nomineeName} <span style={{color: '#a3aed0', fontWeight: '600'}}>({viewStaff.relationship})</span></div>
                            <div style={{ fontSize: '13px', color: '#4f46e5', fontWeight: '700', marginTop: '4px' }}>{viewStaff.nomineePhone}</div>
                        </div>
                    </div>
                    <button onClick={() => setShowViewModal(false)} style={{ ...styles.btnPrimary, width: '100%', marginTop: '35px', justifyContent: 'center', height: '54px' }}>Back to Directory</button>
                </div>
            </div>
        )}

      </div>
    </>
  );
};

export default Staff;